import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Play, Pause, Heart, MessageCircle, Share2, Upload, X, ChevronLeft, ChevronRight,
  Flame, DollarSign, Eye, Star, Filter, MoreVertical, Trash2, Award, Send, Volume2, VolumeX, Youtube, Link2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API = window.location.origin + '/api';

const CATEGORIES = ["Bundle Rules", "Wins", "Money Games", "Highlights", "Tips"];

const CATEGORY_COLORS = {
  "Bundle Rules": "bg-bundle-green text-white",
  "Wins": "bg-bundle-gold text-black",
  "Money Games": "bg-green-600 text-white",
  "Highlights": "bg-blue-500 text-white",
  "Tips": "bg-purple-500 text-white",
};

export default function BundleTV() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [videos, setVideos] = useState([]);
  const [featuredVideos, setFeaturedVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [user, setUser] = useState(null);
  const [featuredIndex, setFeaturedIndex] = useState(0);

  // Fetch user
  useEffect(() => {
    fetch(`${API}/auth/me`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(setUser)
      .catch(() => setUser(null));
  }, []);

  // Fetch videos
  const fetchVideos = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (selectedCategory) params.append('category', selectedCategory);
      
      const [videosRes, featuredRes] = await Promise.all([
        fetch(`${API}/bundle-tv/videos?${params}`, { credentials: 'include' }),
        fetch(`${API}/bundle-tv/videos/featured`, { credentials: 'include' })
      ]);
      
      if (videosRes.ok) {
        const data = await videosRes.json();
        setVideos(data.videos || []);
      }
      
      if (featuredRes.ok) {
        const data = await featuredRes.json();
        setFeaturedVideos(data.videos || []);
      }
    } catch (err) {
      console.error('Failed to fetch videos:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedCategory]);

  useEffect(() => {
    fetchVideos();
  }, [fetchVideos]);

  // Check for video param in URL
  useEffect(() => {
    const videoId = searchParams.get('video');
    if (videoId && videos.length > 0) {
      const video = videos.find(v => v.video_id === videoId);
      if (video) setSelectedVideo(video);
    }
  }, [searchParams, videos]);

  // Auto-rotate featured videos
  useEffect(() => {
    if (featuredVideos.length <= 1) return;
    const interval = setInterval(() => {
      setFeaturedIndex(i => (i + 1) % featuredVideos.length);
    }, 8000);
    return () => clearInterval(interval);
  }, [featuredVideos.length]);

  const handleLike = async (videoId, e) => {
    e?.stopPropagation();
    try {
      const res = await fetch(`${API}/bundle-tv/videos/${videoId}/like`, {
        method: 'POST',
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setVideos(prev => prev.map(v => 
          v.video_id === videoId 
            ? { ...v, likes: data.liked ? [...(v.likes || []), user?.user_id] : (v.likes || []).filter(id => id !== user?.user_id) }
            : v
        ));
      }
    } catch (err) {
      toast.error('Failed to like video');
    }
  };

  const handleReaction = async (videoId, reaction) => {
    try {
      const res = await fetch(`${API}/bundle-tv/videos/${videoId}/react`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ reaction })
      });
      if (res.ok) {
        const data = await res.json();
        setVideos(prev => prev.map(v => 
          v.video_id === videoId ? { ...v, reactions: data.reactions } : v
        ));
        if (selectedVideo?.video_id === videoId) {
          setSelectedVideo(prev => ({ ...prev, reactions: data.reactions }));
        }
      }
    } catch (err) {
      toast.error('Failed to add reaction');
    }
  };

  const handleDelete = async (videoId) => {
    if (!window.confirm('Delete this video? This cannot be undone.')) return;
    try {
      const res = await fetch(`${API}/bundle-tv/videos/${videoId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (res.ok) {
        setVideos(prev => prev.filter(v => v.video_id !== videoId));
        setSelectedVideo(null);
        toast.success('Video deleted');
      }
    } catch (err) {
      toast.error('Failed to delete video');
    }
  };

  const handleFeature = async (videoId) => {
    try {
      const res = await fetch(`${API}/bundle-tv/videos/${videoId}/feature`, {
        method: 'POST',
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.is_featured ? 'Video featured!' : 'Video unfeatured');
        fetchVideos();
      }
    } catch (err) {
      toast.error('Failed to update featured status');
    }
  };

  return (
    <div className="min-h-screen bg-black pb-mobile-nav">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-black/90 backdrop-blur-md border-b border-white/10">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="p-2 -ml-2 hover:bg-white/10 rounded-full">
              <ChevronLeft className="w-5 h-5 text-white" />
            </button>
            <div>
              <h1 className="font-heading text-xl font-bold text-white">Bundle TV</h1>
              <p className="text-xs text-gray-400">Golf videos from the community</p>
            </div>
          </div>
          <Button
            onClick={() => setShowUploadModal(true)}
            className="bg-bundle-green hover:bg-bundle-green/90 text-white gap-2"
            data-testid="upload-video-btn"
          >
            <Upload className="w-4 h-4" />
            <span className="hidden sm:inline">Upload</span>
          </Button>
        </div>

        {/* Category Filter */}
        <div className="flex gap-2 px-4 pb-3 overflow-x-auto scrollbar-hide">
          <button
            onClick={() => setSelectedCategory(null)}
            className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
              !selectedCategory ? 'bg-bundle-green text-white' : 'bg-white/10 text-gray-300 hover:bg-white/20'
            }`}
          >
            All
          </button>
          {CATEGORIES.map(cat => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                selectedCategory === cat ? CATEGORY_COLORS[cat] : 'bg-white/10 text-gray-300 hover:bg-white/20'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      {/* Featured Videos Carousel */}
      {featuredVideos.length > 0 && !selectedCategory && (
        <div className="relative px-4 py-4">
          <div className="flex items-center gap-2 mb-3">
            <Star className="w-5 h-5 text-bundle-gold" />
            <span className="font-heading font-bold text-white">Featured</span>
          </div>
          <div className="relative overflow-hidden rounded-2xl">
            <div 
              className="flex transition-transform duration-500"
              style={{ transform: `translateX(-${featuredIndex * 100}%)` }}
            >
              {featuredVideos.map((video, idx) => (
                <div 
                  key={video.video_id}
                  className="min-w-full cursor-pointer"
                  onClick={() => setSelectedVideo(video)}
                >
                  <FeaturedVideoCard video={video} />
                </div>
              ))}
            </div>
            {featuredVideos.length > 1 && (
              <>
                <button 
                  onClick={(e) => { e.stopPropagation(); setFeaturedIndex(i => (i - 1 + featuredVideos.length) % featuredVideos.length); }}
                  className="absolute left-2 top-1/2 -translate-y-1/2 p-2 bg-black/50 rounded-full hover:bg-black/70"
                >
                  <ChevronLeft className="w-5 h-5 text-white" />
                </button>
                <button 
                  onClick={(e) => { e.stopPropagation(); setFeaturedIndex(i => (i + 1) % featuredVideos.length); }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-black/50 rounded-full hover:bg-black/70"
                >
                  <ChevronRight className="w-5 h-5 text-white" />
                </button>
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                  {featuredVideos.map((_, idx) => (
                    <div 
                      key={idx}
                      className={`w-2 h-2 rounded-full transition-colors ${idx === featuredIndex ? 'bg-white' : 'bg-white/40'}`}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Video Feed */}
      <div className="px-4 py-2">
        {loading ? (
          <div className="grid grid-cols-2 gap-3">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="aspect-[9/16] bg-white/5 rounded-xl animate-pulse" />
            ))}
          </div>
        ) : videos.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-16 h-16 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-4">
              <Play className="w-8 h-8 text-gray-500" />
            </div>
            <p className="text-gray-400 font-body">No videos yet</p>
            <p className="text-gray-500 text-sm mt-1">Be the first to upload!</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {videos.map(video => (
              <VideoCard 
                key={video.video_id} 
                video={video} 
                user={user}
                onSelect={() => setSelectedVideo(video)}
                onLike={(e) => handleLike(video.video_id, e)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <UploadModal 
          onClose={() => setShowUploadModal(false)}
          onSuccess={() => {
            setShowUploadModal(false);
            fetchVideos();
          }}
        />
      )}

      {/* Video Detail Modal */}
      {selectedVideo && (
        <VideoDetailModal
          video={selectedVideo}
          user={user}
          onClose={() => setSelectedVideo(null)}
          onLike={() => handleLike(selectedVideo.video_id)}
          onReaction={(r) => handleReaction(selectedVideo.video_id, r)}
          onDelete={() => handleDelete(selectedVideo.video_id)}
          onFeature={() => handleFeature(selectedVideo.video_id)}
          onUpdate={(updated) => {
            setVideos(prev => prev.map(v => v.video_id === updated.video_id ? updated : v));
            setSelectedVideo(updated);
          }}
        />
      )}
    </div>
  );
}

// Featured Video Card
function FeaturedVideoCard({ video }) {
  return (
    <div className="relative aspect-video bg-gradient-to-br from-bundle-green/20 to-black rounded-2xl overflow-hidden">
      <VideoPlayer video={video} autoPlay muted className="absolute inset-0" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 p-4">
        <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium mb-2 ${CATEGORY_COLORS[video.category] || 'bg-gray-600 text-white'}`}>
          {video.category}
        </span>
        <h3 className="font-heading font-bold text-white text-lg line-clamp-2">{video.title}</h3>
        <div className="flex items-center gap-2 mt-2">
          <img src={video.user_picture || '/default-avatar.png'} alt="" className="w-6 h-6 rounded-full" />
          <span className="text-sm text-gray-300">{video.user_name}</span>
        </div>
      </div>
    </div>
  );
}

// Video Card for Grid
function VideoCard({ video, user, onSelect, onLike }) {
  const isLiked = video.likes?.includes(user?.user_id);
  
  return (
    <div 
      className="relative aspect-[9/16] bg-white/5 rounded-xl overflow-hidden cursor-pointer group"
      onClick={onSelect}
      data-testid={`video-card-${video.video_id}`}
    >
      <VideoPlayer video={video} className="absolute inset-0" previewOnly />
      
      {/* Overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
      
      {/* Category Badge */}
      <span className={`absolute top-2 left-2 px-2 py-0.5 rounded text-[10px] font-medium ${CATEGORY_COLORS[video.category] || 'bg-gray-600 text-white'}`}>
        {video.category}
      </span>

      {/* YouTube/Vimeo Badge */}
      {video.embed_platform && (
        <span className={`absolute top-2 right-2 px-2 py-0.5 rounded text-[10px] font-medium flex items-center gap-1 ${
          video.embed_platform === 'youtube' ? 'bg-red-600 text-white' : 'bg-blue-500 text-white'
        }`}>
          {video.embed_platform === 'youtube' ? <Youtube className="w-3 h-3" /> : <Link2 className="w-3 h-3" />}
          {video.embed_platform === 'youtube' ? 'YT' : 'Vimeo'}
        </span>
      )}
      
      {/* Stats */}
      <div className="absolute bottom-0 left-0 right-0 p-3">
        <p className="text-white text-sm font-medium line-clamp-2 mb-2">{video.title}</p>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1">
            <img src={video.user_picture || '/default-avatar.png'} alt="" className="w-5 h-5 rounded-full" />
            <span className="text-xs text-gray-300 truncate max-w-[80px]">{video.user_name}</span>
          </div>
          <div className="flex items-center gap-3 text-gray-300">
            <button 
              onClick={onLike}
              className={`flex items-center gap-1 ${isLiked ? 'text-red-500' : ''}`}
            >
              <Heart className={`w-4 h-4 ${isLiked ? 'fill-current' : ''}`} />
              <span className="text-xs">{video.likes?.length || 0}</span>
            </button>
            <div className="flex items-center gap-1">
              <Eye className="w-4 h-4" />
              <span className="text-xs">{video.view_count || 0}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Video Player Component
function VideoPlayer({ video, autoPlay = false, muted = true, className = '', previewOnly = false }) {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(autoPlay);
  const [isMuted, setIsMuted] = useState(muted);

  useEffect(() => {
    if (videoRef.current && autoPlay) {
      videoRef.current.play().catch(() => {});
    }
  }, [autoPlay]);

  const togglePlay = (e) => {
    e?.stopPropagation();
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const toggleMute = (e) => {
    e?.stopPropagation();
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  // Handle YouTube/Vimeo embeds
  if (video.embed_platform === 'youtube') {
    const embedUrl = `https://www.youtube.com/embed/${video.embed_video_id}?autoplay=${autoPlay ? 1 : 0}&mute=${muted ? 1 : 0}&loop=1&playsinline=1`;
    return (
      <div className={`${className} relative`}>
        <iframe
          src={embedUrl}
          className="w-full h-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          title={video.title}
        />
      </div>
    );
  }

  if (video.embed_platform === 'vimeo') {
    const embedUrl = `https://player.vimeo.com/video/${video.embed_video_id}?autoplay=${autoPlay ? 1 : 0}&muted=${muted ? 1 : 0}&loop=1&playsinline=1`;
    return (
      <div className={`${className} relative`}>
        <iframe
          src={embedUrl}
          className="w-full h-full"
          allow="autoplay; fullscreen; picture-in-picture"
          allowFullScreen
          title={video.title}
        />
      </div>
    );
  }

  // For uploaded videos, use the streaming endpoint
  const videoUrl = video.video_path ? `${API}/bundle-tv/stream/${video.video_id}` : null;

  if (!videoUrl) {
    return (
      <div className={`${className} bg-gray-800 flex items-center justify-center`}>
        <Play className="w-12 h-12 text-gray-500" />
      </div>
    );
  }

  return (
    <div className={`${className} relative`}>
      <video
        ref={videoRef}
        src={videoUrl}
        className="w-full h-full object-cover"
        loop
        muted={isMuted}
        playsInline
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
      />
      {!previewOnly && (
        <>
          <button 
            onClick={togglePlay}
            className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 hover:opacity-100 transition-opacity"
          >
            {isPlaying ? (
              <Pause className="w-16 h-16 text-white/80" />
            ) : (
              <Play className="w-16 h-16 text-white/80" />
            )}
          </button>
          <button 
            onClick={toggleMute}
            className="absolute bottom-4 right-4 p-2 bg-black/50 rounded-full"
          >
            {isMuted ? <VolumeX className="w-5 h-5 text-white" /> : <Volume2 className="w-5 h-5 text-white" />}
          </button>
        </>
      )}
    </div>
  );
}

// Video Detail Modal
function VideoDetailModal({ video, user, onClose, onLike, onReaction, onDelete, onFeature, onUpdate }) {
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [showActions, setShowActions] = useState(false);
  const isOwner = user?.user_id === video.user_id;
  const isAdmin = user?.is_admin;
  const isLiked = video.likes?.includes(user?.user_id);

  useEffect(() => {
    fetchComments();
  }, [video.video_id]);

  const fetchComments = async () => {
    try {
      const res = await fetch(`${API}/bundle-tv/videos/${video.video_id}/comments`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setComments(data.comments || []);
      }
    } catch (err) {
      console.error('Failed to fetch comments:', err);
    }
  };

  const submitComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    
    try {
      const res = await fetch(`${API}/bundle-tv/videos/${video.video_id}/comments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ content: newComment })
      });
      if (res.ok) {
        setNewComment('');
        fetchComments();
        onUpdate({ ...video, comment_count: (video.comment_count || 0) + 1 });
      }
    } catch (err) {
      toast.error('Failed to post comment');
    }
  };

  const deleteComment = async (commentId) => {
    try {
      const res = await fetch(`${API}/bundle-tv/videos/${video.video_id}/comments/${commentId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (res.ok) {
        fetchComments();
        onUpdate({ ...video, comment_count: Math.max(0, (video.comment_count || 1) - 1) });
      }
    } catch (err) {
      toast.error('Failed to delete comment');
    }
  };

  const getReactionCount = (reaction) => {
    return video.reactions?.[reaction]?.length || 0;
  };

  const hasReacted = (reaction) => {
    return video.reactions?.[reaction]?.includes(user?.user_id);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black">
      {/* Close Button */}
      <button 
        onClick={onClose}
        className="absolute top-4 left-4 z-50 p-2 bg-black/50 rounded-full hover:bg-black/70"
      >
        <X className="w-6 h-6 text-white" />
      </button>

      {/* Actions Menu */}
      <div className="absolute top-4 right-4 z-50">
        <button 
          onClick={() => setShowActions(!showActions)}
          className="p-2 bg-black/50 rounded-full hover:bg-black/70"
        >
          <MoreVertical className="w-6 h-6 text-white" />
        </button>
        {showActions && (
          <div className="absolute right-0 mt-2 w-48 bg-gray-900 rounded-lg shadow-xl border border-white/10 overflow-hidden">
            {isAdmin && (
              <button 
                onClick={() => { onFeature(); setShowActions(false); }}
                className="w-full px-4 py-3 text-left text-sm text-white hover:bg-white/10 flex items-center gap-2"
              >
                <Award className="w-4 h-4" />
                {video.is_featured ? 'Remove from Featured' : 'Add to Featured'}
              </button>
            )}
            {(isOwner || isAdmin) && (
              <button 
                onClick={() => { onDelete(); setShowActions(false); }}
                className="w-full px-4 py-3 text-left text-sm text-red-400 hover:bg-white/10 flex items-center gap-2"
              >
                <Trash2 className="w-4 h-4" />
                Delete Video
              </button>
            )}
          </div>
        )}
      </div>

      {/* Video */}
      <div className="h-[55vh] bg-black">
        <VideoPlayer video={video} autoPlay={false} muted={false} className="w-full h-full" />
      </div>

      {/* Info & Comments */}
      <div className="h-[45vh] bg-gray-900 overflow-y-auto">
        <div className="p-4 border-b border-white/10">
          {/* User Info */}
          <div className="flex items-center gap-3 mb-3">
            <img src={video.user_picture || '/default-avatar.png'} alt="" className="w-10 h-10 rounded-full" />
            <div>
              <p className="font-medium text-white">{video.user_name}</p>
              <p className="text-xs text-gray-400">{new Date(video.created_at).toLocaleDateString()}</p>
            </div>
            <span className={`ml-auto px-2 py-0.5 rounded text-xs font-medium ${CATEGORY_COLORS[video.category] || 'bg-gray-600 text-white'}`}>
              {video.category}
            </span>
          </div>

          {/* Title & Description */}
          <h2 className="font-heading font-bold text-white text-lg mb-1">{video.title}</h2>
          {video.description && <p className="text-gray-400 text-sm">{video.description}</p>}

          {/* Actions */}
          <div className="flex items-center gap-4 mt-4">
            <button 
              onClick={onLike}
              className={`flex items-center gap-2 px-4 py-2 rounded-full transition-colors ${
                isLiked ? 'bg-red-500/20 text-red-500' : 'bg-white/10 text-white hover:bg-white/20'
              }`}
            >
              <Heart className={`w-5 h-5 ${isLiked ? 'fill-current' : ''}`} />
              <span>{video.likes?.length || 0}</span>
            </button>
            
            {/* Reactions */}
            <div className="flex items-center gap-1">
              {['🔥', '💰', '👀'].map(emoji => (
                <button
                  key={emoji}
                  onClick={() => onReaction(emoji)}
                  className={`px-3 py-2 rounded-full text-lg transition-colors ${
                    hasReacted(emoji) ? 'bg-bundle-gold/20' : 'bg-white/10 hover:bg-white/20'
                  }`}
                >
                  {emoji} <span className="text-sm text-white ml-1">{getReactionCount(emoji)}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Comments */}
        <div className="p-4">
          <h3 className="font-heading font-bold text-white mb-3">Comments ({comments.length})</h3>
          
          {/* Comment Input */}
          <form onSubmit={submitComment} className="flex gap-2 mb-4">
            <input
              type="text"
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="Add a comment..."
              className="flex-1 bg-white/10 border border-white/20 rounded-full px-4 py-2 text-white placeholder-gray-500 focus:outline-none focus:border-bundle-green"
            />
            <button 
              type="submit"
              disabled={!newComment.trim()}
              className="p-2 bg-bundle-green rounded-full disabled:opacity-50"
            >
              <Send className="w-5 h-5 text-white" />
            </button>
          </form>

          {/* Comment List */}
          <div className="space-y-3">
            {comments.map(comment => (
              <div key={comment.comment_id} className="flex gap-3">
                <img src={comment.user_picture || '/default-avatar.png'} alt="" className="w-8 h-8 rounded-full flex-shrink-0" />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-white text-sm">{comment.user_name}</span>
                    <span className="text-xs text-gray-500">{new Date(comment.created_at).toLocaleDateString()}</span>
                    {(comment.user_id === user?.user_id || isAdmin) && (
                      <button 
                        onClick={() => deleteComment(comment.comment_id)}
                        className="ml-auto text-gray-500 hover:text-red-400"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                  <p className="text-gray-300 text-sm">{comment.content}</p>
                </div>
              </div>
            ))}
            {comments.length === 0 && (
              <p className="text-center text-gray-500 py-4">No comments yet. Be the first!</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Upload Modal
function UploadModal({ onClose, onSuccess }) {
  const [uploadType, setUploadType] = useState('file'); // 'file' or 'link'
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [videoFile, setVideoFile] = useState(null);
  const [videoUrl, setVideoUrl] = useState('');
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const videoInputRef = useRef(null);
  const thumbnailInputRef = useRef(null);
  const formRef = useRef(null);

  // Scroll form to top when switching tabs
  const handleTabSwitch = (type) => {
    setUploadType(type);
    if (formRef.current) {
      formRef.current.scrollTop = 0;
    }
  };

  const handleVideoSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (1.5GB limit)
      if (file.size > 1.5 * 1024 * 1024 * 1024) {
        toast.error('Video must be under 1.5GB');
        return;
      }
      setVideoFile(file);
    }
  };

  const isValidVideoUrl = (url) => {
    const patterns = [
      /youtube\.com\/watch\?v=/,
      /youtu\.be\//,
      /youtube\.com\/embed\//,
      /youtube\.com\/shorts\//,
      /vimeo\.com\/\d+/,
      /player\.vimeo\.com\/video\//,
    ];
    return patterns.some(pattern => pattern.test(url));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!title || !category) {
      toast.error('Please fill in title and category');
      return;
    }

    if (uploadType === 'file' && !videoFile) {
      toast.error('Please select a video file');
      return;
    }

    if (uploadType === 'link' && !videoUrl) {
      toast.error('Please enter a video URL');
      return;
    }

    if (uploadType === 'link' && !isValidVideoUrl(videoUrl)) {
      toast.error('Please enter a valid YouTube or Vimeo URL');
      return;
    }

    setUploading(true);
    setProgress(10);

    try {
      let res;
      
      if (uploadType === 'file') {
        const formData = new FormData();
        formData.append('title', title);
        formData.append('description', description);
        formData.append('category', category);
        formData.append('video', videoFile);
        if (thumbnailFile) {
          formData.append('thumbnail', thumbnailFile);
        }

        setProgress(30);

        res = await fetch(`${API}/bundle-tv/videos/upload`, {
          method: 'POST',
          credentials: 'include',
          body: formData
        });
      } else {
        // Link upload
        setProgress(50);
        res = await fetch(`${API}/bundle-tv/videos/link`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            title,
            description,
            category,
            video_url: videoUrl
          })
        });
      }

      setProgress(90);

      if (res.ok) {
        toast.success(uploadType === 'file' ? 'Video uploaded successfully!' : 'Video link posted successfully!');
        onSuccess();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Upload failed');
      }
    } catch (err) {
      console.error('Upload error:', err);
      toast.error('Failed to upload video');
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80">
      <div className="w-full max-w-lg bg-gray-900 rounded-2xl shadow-xl border border-white/10 overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <h2 className="font-heading font-bold text-white text-lg">Upload Video</h2>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full">
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        <form ref={formRef} onSubmit={handleSubmit} className="p-4 space-y-4 max-h-[70vh] overflow-y-auto">
          {/* Upload Type Toggle */}
          <div className="flex gap-2 p-1 bg-white/5 rounded-lg">
            <button
              type="button"
              onClick={() => handleTabSwitch('file')}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                uploadType === 'file' ? 'bg-bundle-green text-white' : 'text-gray-400 hover:text-white'
              }`}
            >
              Upload File
            </button>
            <button
              type="button"
              onClick={() => handleTabSwitch('link')}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                uploadType === 'link' ? 'bg-bundle-green text-white' : 'text-gray-400 hover:text-white'
              }`}
            >
              YouTube / Vimeo
            </button>
          </div>

          {/* Video Upload or Link Input */}
          {uploadType === 'file' ? (
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Video File *</label>
              <input
                ref={videoInputRef}
                type="file"
                accept="video/mp4,video/quicktime,video/webm,video/x-m4v"
                onChange={handleVideoSelect}
                className="hidden"
              />
              <button
                type="button"
                onClick={() => videoInputRef.current?.click()}
                className="w-full p-6 border-2 border-dashed border-white/20 rounded-xl text-center hover:border-bundle-green/50 transition-colors"
              >
                {videoFile ? (
                  <div className="text-bundle-green">
                    <Play className="w-8 h-8 mx-auto mb-2" />
                    <p className="font-medium">{videoFile.name}</p>
                    <p className="text-sm text-gray-400">{(videoFile.size / (1024 * 1024)).toFixed(1)} MB</p>
                  </div>
                ) : (
                  <div className="text-gray-400">
                    <Upload className="w-8 h-8 mx-auto mb-2" />
                    <p>Tap to select video</p>
                    <p className="text-xs mt-1">MP4, MOV, WebM • Max 1.5GB</p>
                  </div>
                )}
              </button>
            </div>
          ) : (
            <div className="bg-bundle-green/5 border border-bundle-green/30 rounded-xl p-4">
              <label className="block text-sm font-medium text-bundle-green mb-2">Video URL * (Required)</label>
              <input
                type="url"
                value={videoUrl}
                onChange={(e) => setVideoUrl(e.target.value)}
                placeholder="https://youtube.com/watch?v=... or https://vimeo.com/..."
                className="w-full bg-white/10 border-2 border-white/30 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-bundle-green text-base"
              />
              <p className="text-xs text-gray-400 mt-2">Paste a YouTube or Vimeo link above</p>
            </div>
          )}

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Title *</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter video title"
              className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white placeholder-gray-500 focus:outline-none focus:border-bundle-green"
              maxLength={200}
            />
          </div>

          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Category *</label>
            <div className="grid grid-cols-2 gap-2">
              {CATEGORIES.map(cat => (
                <button
                  key={cat}
                  type="button"
                  onClick={() => setCategory(cat)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    category === cat 
                      ? CATEGORY_COLORS[cat] 
                      : 'bg-white/10 text-gray-300 hover:bg-white/20'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Add a description..."
              rows={3}
              className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white placeholder-gray-500 focus:outline-none focus:border-bundle-green resize-none"
              maxLength={1000}
            />
          </div>

          {/* Thumbnail (Optional) */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Thumbnail (optional)</label>
            <input
              ref={thumbnailInputRef}
              type="file"
              accept="image/*"
              onChange={(e) => setThumbnailFile(e.target.files[0])}
              className="hidden"
            />
            <button
              type="button"
              onClick={() => thumbnailInputRef.current?.click()}
              className="w-full p-3 border border-white/20 rounded-lg text-center hover:border-bundle-green/50 transition-colors"
            >
              {thumbnailFile ? (
                <span className="text-bundle-green">{thumbnailFile.name}</span>
              ) : (
                <span className="text-gray-400">+ Add custom thumbnail</span>
              )}
            </button>
          </div>

          {/* Progress Bar */}
          {uploading && (
            <div className="w-full bg-white/10 rounded-full h-2 overflow-hidden">
              <div 
                className="bg-bundle-green h-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          )}

          {/* Submit */}
          <Button
            type="submit"
            disabled={uploading || !title || !category || (uploadType === 'file' ? !videoFile : !videoUrl)}
            className="w-full bg-bundle-green hover:bg-bundle-green/90 text-white py-3"
          >
            {uploading ? 'Uploading...' : (uploadType === 'file' ? 'Upload Video' : 'Post Video Link')}
          </Button>
        </form>
      </div>
    </div>
  );
}
